import { useCallback, useState } from 'react';
import { useStripePromise } from '@frontend/payments-stripe-wrapper';
import type {
  PaymentMethodSetupApi,
  PaymentMethodSetupEventHandler,
  PaymentMethodSetupGatewayConfig,
  PaymentMethodSetupKind,
  PaymentMethodSetupSession,
} from '../types';

export type UsePreparePaymentMethodSetupArgs = {
  kind: PaymentMethodSetupKind;
  api: PaymentMethodSetupApi;
  gatewayConfig: PaymentMethodSetupGatewayConfig;
  /** Off to skip loading the processor SDK, e.g. when setup can't open. */
  enabled?: boolean;
  onEvent?: PaymentMethodSetupEventHandler;
};

/**
 * Creates the setup session and loads the processor SDK before the setup UI opens, so a trigger can show its own
 * loading state and open the modal with everything ready. Pass the session to `PaymentMethodSetupModal`.
 */
export const usePreparePaymentMethodSetup = ({
  kind,
  api,
  gatewayConfig,
  enabled = true,
  onEvent,
}: UsePreparePaymentMethodSetupArgs) => {
  const { stripePromise } = useStripePromise({
    stripeExpressKey: enabled ? gatewayConfig.stripe?.publishableKey ?? '' : '',
  });
  const [session, setSession] = useState<PaymentMethodSetupSession>();
  const [isPreparing, setIsPreparing] = useState(false);
  const [hasError, setHasError] = useState(false);

  const prepare = useCallback(async () => {
    setIsPreparing(true);
    setHasError(false);
    try {
      const [created, stripe] = await Promise.all([api.createSetupSession(kind), stripePromise]);
      if (created.gateway === 'stripe' && !stripe) throw new Error('processor_not_ready');
      setSession(created);
      return created;
    } catch {
      onEvent?.('setup_session_failed');
      setHasError(true);
      return undefined;
    } finally {
      setIsPreparing(false);
    }
  }, [api, kind, onEvent, stripePromise]);

  /** Drops the session once the setup UI closes; a secret can only be confirmed once. */
  const reset = useCallback(() => {
    setSession(undefined);
    setHasError(false);
  }, []);

  return { prepare, reset, session, isPreparing, hasError };
};
